import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/store';
import { requestWithdrawal, fetchBalance } from '@/store/slices/walletSlice';
import { walletApi } from '@/services/api/wallet';
import { FiX, FiAlertCircle } from 'react-icons/fi';
import { Currency, WithdrawalMethod } from '@/types/wallet';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useCurrency } from '@/contexts/CurrencyContext';

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { balance, actionLoading, settings } = useSelector((state: RootState) => state.wallet);
  const { userCurrency } = useCurrency();

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(userCurrency as Currency);
  const [withdrawalType, setWithdrawalType] = useState<'bank' | 'mobile_money'>('bank');
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [mobileAccounts, setMobileAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [newAccount, setNewAccount] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    bankCode: '',
    provider: 'mtn',
    phoneNumber: '',
  });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [fee, setFee] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadSavedAccounts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const run = async () => {
      const amt = parseFloat(amount) || 0;
      if (amt <= 0) {
        setFee(0);
        return;
      }

      try {
        const method = withdrawalType === 'bank' ? 'bank_transfer' : 'mobile_money';
        const provider = withdrawalType === 'mobile_money' ? accountDetails.provider : undefined;

        const response = await walletApi.calculateFeePreview({
          amount: amt,
          type: 'withdrawal',
          method,
          provider,
          currency,
        });

        if (response.data?.success) {
          setFee(response.data.data?.finalFee || 0);
        } else {
          setFee(0);
        }
      } catch (error) {
        setFee(0);
      }
    };

    run();
  }, [isOpen, amount, currency, withdrawalType, accountDetails.provider]);

  const loadSavedAccounts = () => {
    if (settings?.balances) {
      const banks = settings.balances.filter((b: any) => b.type === 'bank');
      const mobile = settings.balances.filter((b: any) => b.type === 'mobile_money');
      setBankAccounts(banks);
      setMobileAccounts(mobile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    if (!balance || parseFloat(amount) > balance.available) {
      return;
    }

    const withdrawalMethod: WithdrawalMethod = {
      type: withdrawalType === 'bank' ? 'bank_transfer' : 'mobile_money',
      accountName: accountDetails.accountName,
      accountNumber: accountDetails.accountNumber,
      ...(withdrawalType === 'bank' && {
        bankName: accountDetails.bankName,
        bankCode: accountDetails.bankCode,
      }),
      ...(withdrawalType === 'mobile_money' && {
        provider: accountDetails.provider as any,
      }),
    };

    const result = await dispatch(
      requestWithdrawal({
        amount: parseFloat(amount),
        currency,
        withdrawalMethod,
        twoFactorCode: twoFactorCode || undefined,
      })
    );

    if (result.meta.requestStatus === 'fulfilled') {
      onClose();
      dispatch(fetchBalance(currency));
    }
  };

  if (!isOpen) return null;

  const availableBalance = balance?.available || 0;
  const minWithdrawal = 10;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-gray-800 sm:p-5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
            Withdraw Funds
          </h2>
          <button
            onClick={onClose}
            title="Close withdraw modal"
            className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:p-5">
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-800 dark:bg-cyan-900/20">
            <div className="flex gap-3">
              <FiAlertCircle className="mt-0.5 flex-shrink-0 text-cyan-600 dark:text-cyan-300" />
              <div className="text-sm text-cyan-800 dark:text-cyan-200">
                <p className="font-medium mb-1">Withdrawal Process</p>
                <p>Your withdrawal request will be reviewed by our team and processed within 24-48 hours.</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
            <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">Available Balance</p>
            <p className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
              {availableBalance.toFixed(2)} {currency}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
              Withdrawal Amount
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min={minWithdrawal}
              max={availableBalance}
              required
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
            <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
              Minimum: {minWithdrawal} {currency} | Maximum: {availableBalance.toFixed(2)} {currency}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
              Withdrawal Method
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setWithdrawalType('bank')}
                className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                  withdrawalType === 'bank'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-slate-300 text-slate-700 dark:border-gray-700 dark:text-gray-300'
                }`}
              >
                Bank Account
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalType('mobile_money')}
                className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                  withdrawalType === 'mobile_money'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-slate-300 text-slate-700 dark:border-gray-700 dark:text-gray-300'
                }`}
              >
                Mobile Money
              </button>
            </div>
          </div>

          {withdrawalType === 'bank' && (
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Account Name
                </label>
                <input
                  type="text"
                  value={accountDetails.accountName}
                  onChange={(e) => setAccountDetails({ ...accountDetails, accountName: e.target.value })}
                  required
                  title="Account name"
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Account Number
                </label>
                <input
                  type="text"
                  value={accountDetails.accountNumber}
                  onChange={(e) => setAccountDetails({ ...accountDetails, accountNumber: e.target.value })}
                  required
                  title="Account number"
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={accountDetails.bankName}
                  onChange={(e) => setAccountDetails({ ...accountDetails, bankName: e.target.value })}
                  required
                  title="Bank name"
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          )}

          {withdrawalType === 'mobile_money' && (
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Provider
                </label>
                <select
                  value={accountDetails.provider}
                  onChange={(e) => setAccountDetails({ ...accountDetails, provider: e.target.value })}
                  title="Mobile money provider"
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="mtn">MTN Mobile Money</option>
                  <option value="vodafone">Vodafone Cash</option>
                  <option value="airteltigo">AirtelTigo Money</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={accountDetails.phoneNumber}
                  onChange={(e) => setAccountDetails({ ...accountDetails, phoneNumber: e.target.value, accountNumber: e.target.value, accountName: e.target.value })}
                  placeholder="0XXXXXXXXX"
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
            <h4 className="mb-2 font-medium text-slate-900 dark:text-white">Summary</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-gray-400">Withdrawal Amount:</span>
                <span className="font-medium text-slate-900 dark:text-white">
                  {(parseFloat(amount) || 0).toFixed(2)} {currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-gray-400">Processing Fee:</span>
                <span className="font-medium text-slate-900 dark:text-white">
                  {fee.toFixed(2)} {currency}
                </span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2 dark:border-gray-800">
                <span className="font-medium text-slate-900 dark:text-white">You'll Receive:</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {((parseFloat(amount) || 0) - fee).toFixed(2)} {currency}
                </span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={
              actionLoading ||
              !amount ||
              parseFloat(amount) < minWithdrawal ||
              parseFloat(amount) > availableBalance ||
              !accountDetails.accountName ||
              !accountDetails.accountNumber
            }
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLoading ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                Processing...
              </>
            ) : (
              'Submit Withdrawal Request'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WithdrawModal;
